/**
 * IndexUtilizationCell.tsx
 * Item 6.1 — one day's % cell for the lab-wide Index-machine row, sized to
 * line up with the day columns of whichever grid mounts it (Machine
 * Schedule today; Master Gantt at Item 6.2 shares the same day math).
 *
 * Deliberately generic/cross-view (placed in `components/schedule/`, per
 * CODING-STANDARDS shared-component convention) — unlike
 * `views/scenario/index/IndexCapacityHeatmapCell.tsx` (which additionally
 * renders the scenario-owned "own tentative" split + Compare-to-Baseline
 * ghost badge, both meaningless outside a scenario plan), this cell only
 * knows about the BASELINE/lab-wide firm/soft split (I13) — no "own scenario"
 * framing. Re-uses `heatmapColorToken` (the same band→token mapping the
 * scenario cell and the Employee Schedule heatmap use) so all three heatmaps
 * read the same color language.
 *
 * Non-working days (weekends / stat holidays) render a muted, unlabeled
 * veil — same visual intent as `MachineScheduleNonWorkOverlay` on the
 * booking rows.
 */

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { heatmapColorToken } from './CapacityHeatmapCell';
import { buildIndexRowTooltipLines, type IndexRowTooltipDay } from '../../utils/indexUtilizationTooltip';

export interface IndexUtilizationCellProps {
  day: IndexRowTooltipDay;
  /** Column width in px — pass the grid's `dayColWidth` so cells line up with the day header. */
  width: number;
  /** Row height in px. */
  height: number;
  /** True for today's column — matches the accent border the rest of the grid uses. */
  isToday?: boolean;
}

function TooltipBody({ day }: { day: IndexRowTooltipDay }): React.JSX.Element {
  const lines = buildIndexRowTooltipLines(day);
  return (
    <Box sx={{ fontSize: '0.7rem', lineHeight: 1.6 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', mb: 0.25 }}>{day.date}</Typography>
      {lines.map((line, i) => (
        <Typography key={i} component="div" sx={{ fontSize: '0.7rem', whiteSpace: 'pre' }}>
          {line}
        </Typography>
      ))}
    </Box>
  );
}

export const IndexUtilizationCell: React.FC<IndexUtilizationCellProps> = ({
  day,
  width,
  height,
  isToday = false,
}) => {
  const theme = useTheme();

  const baseSx = {
    flex: 1,
    minWidth: width,
    height,
    flexShrink: 0,
    borderRight: `1px solid ${alpha(theme.border.color, 0.5)}`,
    borderLeft: isToday ? `2px solid ${alpha(theme.schedule.todayColumn, 0.55)}` : undefined,
  } as const;

  if (!day.isWorkingDay) {
    return (
      <Tooltip title={<TooltipBody day={day} />} arrow placement="top">
        <Box
          role="gridcell"
          aria-label={`${day.date}: non-working day`}
          sx={{ ...baseSx, backgroundColor: alpha(theme.schedule.noActivity, 0.14) }}
        />
      </Tooltip>
    );
  }

  // P3 (2026-07-29): the lane reads as busy as its BUSIEST type — never the
  // cross-type sum (two types each at 100% is a full day, not a 200% conflict).
  const token = heatmapColorToken(day.maxTypeFirmPct);
  const bg = token === 'noActivity' ? 'transparent' : alpha(theme.schedule[token], 0.3);
  const fg = token === 'noActivity' ? theme.palette.text.disabled : theme.schedule[token];

  return (
    <Tooltip title={<TooltipBody day={day} />} arrow placement="top">
      <Box
        role="gridcell"
        aria-label={`${day.date}: busiest type ${day.maxTypeFirmPct}% committed${day.maxTypeSoftPct > 0 ? `, +${day.maxTypeSoftPct}% draft` : ''}${day.isOverCapacity ? ', over capacity' : ''}`}
        sx={{
          ...baseSx,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.15,
          backgroundColor: bg,
          boxShadow: day.isOverCapacity ? `inset 0 0 0 2px ${theme.schedule.heatmapHigh}` : 'none',
        }}
      >
        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.68rem', color: fg, lineHeight: 1.1 }}>
          {day.maxTypeFirmPct}%
        </Typography>

        {day.maxTypeSoftPct > 0 && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.52rem',
              fontWeight: 700,
              color: theme.schedule.conflictSoft,
              border: `1px dashed ${theme.schedule.conflictSoft}`,
              borderRadius: '3px',
              px: 0.25,
              lineHeight: 1.2,
            }}
          >
            +{day.maxTypeSoftPct}%
          </Typography>
        )}

        {day.isOverCapacity && (
          <WarningAmberIcon
            aria-label="Over capacity"
            sx={{ position: 'absolute', top: 1, right: 1, fontSize: '0.62rem', color: theme.schedule.heatmapHigh }}
          />
        )}
      </Box>
    </Tooltip>
  );
};
